import { NavLink, Outlet } from 'react-router-dom';
import { useState } from 'react';
import { Package, Truck, Stack, ClockCounterClockwise, Barcode, Wrench, Toolbox, HardHat, ClipboardText, TreeStructure, ChartPieSlice, Buildings, CalendarBlank, DeviceTabletSpeaker, ChartLineUp, ListChecks, Clock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, isSimpleMenu, navFlag } from '@/auth/AuthContext';
import { ModuleSidebar, sidebarPillClass } from '@/components/enterprise/ModuleSidebar';
import ScanDialog from './ScanDialog';
import { DepartmentProvider, useDepartment } from './DepartmentContext';
import { DepartmentSelector, DepartmentPicker } from './DepartmentSelector';

// `flag` が付いている項目は、シンプルメニューが ON のときは隠れる。
// 管理者が 設定 → モジュール設定 でその画面だけ戻せる（画面もデータも消えない）。
const NAV_GROUPS = [
  { items: [
    { to: '/mfg', end: true, key: 'mfg.nav.dashboard', icon: ChartPieSlice, testid: 'mfg-nav-dashboard' },
  ] },
  { label: 'mfg.navGroup.inventory', items: [
    { to: '/mfg/inventory', key: 'mfg.nav.inventory2', icon: Package, testid: 'mfg-nav-inventory' },
    { to: '/mfg/moves', key: 'mfg.nav.moves', icon: ClockCounterClockwise, testid: 'mfg-nav-moves' },
  ] },
  { label: 'mfg.navGroup.production', items: [
    { to: '/mfg/work-orders', key: 'mfg.nav.workOrders', icon: ClipboardText, testid: 'mfg-nav-workorders' },
    { to: '/mfg/shipping', key: 'mfg.nav.shipping', icon: Truck, testid: 'mfg-nav-shipping', flag: 'mfg_shipping' },
    { to: '/mfg/schedule', key: 'mfg.nav.schedule', icon: CalendarBlank, testid: 'mfg-nav-schedule', flag: 'mfg_schedule' },
    { to: '/mfg/analytics', key: 'mfg.nav.analytics', icon: ChartLineUp, testid: 'mfg-nav-analytics', flag: 'mfg_analytics' },
    { to: '/mfg/labor', key: 'mfg.nav.labor', icon: Clock, testid: 'mfg-nav-labor', flag: 'mfg_labor' },
    { to: '/mfg/kiosk', key: 'mfg.nav.kiosk', icon: DeviceTabletSpeaker, testid: 'mfg-nav-kiosk' },
    { to: '/mfg/bom', key: 'mfg.nav.bom', icon: TreeStructure, testid: 'mfg-nav-bom' },
    // 部品表は 1 画面にまとめた（タブ）。シンプルメニュー中は一覧の行を出さない。
    { to: '/mfg/boms', key: 'mfg.nav.bomList', icon: ListChecks, testid: 'mfg-nav-bomlist', merged: true },
  ] },
  { label: 'mfg.navGroup.equipment', items: [
    { to: '/mfg/equipment', key: 'mfg.nav.equipment', icon: Toolbox, testid: 'mfg-nav-equipment' },
    { to: '/mfg/maintenance', key: 'mfg.nav.maintenance', icon: Wrench, testid: 'mfg-nav-maintenance' },
    { to: '/mfg/ppe', key: 'mfg.nav.ppe', icon: HardHat, testid: 'mfg-nav-ppe', flag: 'mfg_ppe' },
  ] },
];

function MfgLayoutInner() {
  const { t } = useT();
  const { user } = useAuth();
  const simple = isSimpleMenu(user);
  const [scanOpen, setScanOpen] = useState(false);
  const { activeId, active, isAll, loading, locked } = useDepartment();

  if (loading) return <div className="py-24 text-center text-sm text-zinc-400" data-testid="mfg-dept-loading">…</div>;
  // A8 · 固定されているのに製造部門が決まらない現場ユーザー（管理者が未設定）
  if (locked && !activeId) {
    return (
      <div className="py-24 text-center" data-testid="mfg-dept-unassigned">
        <p className="text-sm font-semibold text-zinc-700">{t('mfg.dept.unassigned')}</p>
        <p className="mt-1 text-xs text-zinc-500">{t('mfg.dept.unassignedHint')}</p>
      </div>
    );
  }
  if (!activeId) return <DepartmentPicker />;

  const accent = active?.color || '#F97316';
  const sections = NAV_GROUPS.map((g) => ({
    label: g.label ? t(g.label) : null,
    items: g.items
      .filter((i) => !simple || (!i.merged && (!i.flag || navFlag(user, i.flag))))
      .map((i) => ({ ...i, label: t(i.key) })),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex flex-col md:flex-row gap-5 md:gap-6" data-testid="mfg-layout">
      <div className="md:w-64 md:shrink-0 space-y-2">
        <ModuleSidebar
          icon={Buildings}
          title={t('mfg.title')}
          sections={sections}
          testid="mfg-sidebar"
          actions={(
            <Button data-testid="mfg-scan-btn" onClick={() => setScanOpen(true)} variant="outline"
              className="w-full h-10">
              <Barcode size={18} className="mr-2" weight="bold" /> {t('mfg.nav.scan')}
            </Button>
          )}
        />
        <button onClick={() => setScanOpen(true)} data-testid="mfg-scan-btn-mobile"
          className={`md:hidden ${sidebarPillClass({ isActive: false })}`}>
          <Barcode size={15} weight="duotone" /> {t('mfg.nav.scan')}
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-4">
          {isAll
            ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 text-xs font-semibold" data-testid="mfg-dept-banner">
                <Stack size={13} weight="duotone" /> {t('mfg.dept.allBanner')}
              </span>
            : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: accent }} data-testid="mfg-dept-banner">
                <Buildings size={13} weight="duotone" /> {active?.name}
              </span>}
          <DepartmentSelector />
        </div>
        <div key={activeId || 'none'}>
          <Outlet />
        </div>
      </div>
      <ScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
    </div>
  );
}

export default function MfgLayout() {
  return (
    <DepartmentProvider>
      <MfgLayoutInner />
    </DepartmentProvider>
  );
}
